import styled, { createGlobalStyle } from 'styled-components';

export const GlobalStyles = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
      sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background-color: #f5f5f5;
    color: #333;
    line-height: 1.6;
  }

  code {
    font-family: source-code-pro, Menlo, Monaco, Consolas, 'Courier New',
      monospace;
  }

  /* Mobile-first responsive breakpoints */
  :root {
    --mobile: 320px;
    --tablet: 768px;
    --desktop: 1024px;
    --large-desktop: 1440px;
    
    /* Colors */
    --primary-color: #2563eb;
    --primary-hover: #1d4ed8;
    --secondary-color: #64748b;
    --success-color: #10b981;
    --warning-color: #f59e0b;
    --error-color: #ef4444;
    --info-color: #3b82f6;
    --background-color: #f8fafc;
    --background-secondary: #f1f5f9;
    --card-background: #ffffff;
    --border-color: #e2e8f0;
    --text-primary: #1e293b;
    --text-secondary: #64748b;
    
    /* Spacing */
    --spacing-xs: 0.25rem;
    --spacing-sm: 0.5rem;
    --spacing-md: 1rem;
    --spacing-lg: 1.5rem;
    --spacing-xl: 2rem;
    --spacing-2xl: 3rem;
    
    /* Touch targets */
    --touch-target: 44px;
    
    /* Border radius */
    --border-radius-sm: 0.25rem;
    --border-radius-md: 0.5rem;
    --border-radius-lg: 0.75rem;
  }

  /* Ensure minimum touch target size */
  button, input, select, textarea, a {
    min-height: var(--touch-target);
    min-width: var(--touch-target);
  }

  /* Focus styles for accessibility */
  button:focus, input:focus, select:focus, textarea:focus {
    outline: 2px solid var(--primary-color);
    outline-offset: 2px;
  }
`;

// Common styled components
export const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-md);

  @media (min-width: 768px) {
    padding: 0 var(--spacing-lg);
  }

  @media (min-width: 1024px) {
    padding: 0 var(--spacing-xl);
  }
`;

export const Card = styled.div`
  background: var(--card-background);
  border-radius: var(--border-radius-lg);
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  padding: var(--spacing-lg);
  margin-bottom: var(--spacing-md);

  @media (min-width: 768px) {
    padding: var(--spacing-xl);
    margin-bottom: var(--spacing-lg);
  }
`;

export const Button = styled.button<{ 
  $variant?: 'primary' | 'secondary' | 'danger';
  $size?: 'small' | 'medium' | 'large';
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: var(--border-radius-md);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  
  ${props => {
    switch (props.$size) {
      case 'small':
        return `
          padding: var(--spacing-xs) var(--spacing-sm);
          font-size: 0.875rem;
          min-height: 32px;
          min-width: 32px;
        `;
      case 'large':
        return `
          padding: var(--spacing-md) var(--spacing-xl);
          font-size: 1.125rem;
          min-height: 52px;
          min-width: 52px;
        `;
      default:
        return `
          padding: var(--spacing-sm) var(--spacing-lg);
          font-size: 1rem;
          min-height: var(--touch-target);
          min-width: var(--touch-target);
        `;
    }
  }}

  ${props => {
    switch (props.$variant) {
      case 'secondary':
        return `
          background-color: var(--secondary-color);
          color: white;
          &:hover {
            background-color: #475569;
          }
        `;
      case 'danger':
        return `
          background-color: var(--error-color);
          color: white;
          &:hover {
            background-color: #dc2626;
          }
        `;
      default:
        return `
          background-color: var(--primary-color);
          color: white;
          &:hover {
            background-color: var(--primary-hover);
          }
        `;
    }
  }}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const Input = styled.input`
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  font-size: 1rem;
  min-height: var(--touch-target);
  
  &:focus {
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }

  &.error {
    border-color: var(--error-color);
  }
`;

export const TextArea = styled.textarea`
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  font-size: 1rem;
  font-family: inherit;
  line-height: 1.5;
  resize: vertical;
  min-height: 100px;
  
  &:focus {
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }

  &.error {
    border-color: var(--error-color);
  }
`;

export const Label = styled.label`
  display: block;
  margin-bottom: var(--spacing-xs);
  font-weight: 500;
  color: var(--text-primary);
`;

export const ErrorMessage = styled.span`
  color: var(--error-color);
  font-size: 0.875rem;
  margin-top: var(--spacing-xs);
  display: block;
`;

export const Grid = styled.div<{ $columns?: number }>`
  display: grid;
  gap: var(--spacing-md);
  grid-template-columns: 1fr;

  @media (min-width: 768px) {
    grid-template-columns: repeat(${props => props.$columns || 2}, 1fr);
    gap: var(--spacing-lg);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(${props => props.$columns || 3}, 1fr);
  }
`;

export const FlexContainer = styled.div<{ 
  $direction?: 'row' | 'column';
  $justify?: 'flex-start' | 'center' | 'flex-end' | 'space-between' | 'space-around';
  $align?: 'flex-start' | 'center' | 'flex-end' | 'stretch';
  $gap?: string;
}>`
  display: flex;
  flex-direction: ${props => props.$direction || 'row'};
  justify-content: ${props => props.$justify || 'flex-start'};
  align-items: ${props => props.$align || 'stretch'};
  gap: ${props => props.$gap || 'var(--spacing-md)'};

  @media (max-width: 767px) {
    flex-direction: column;
  }
`;